import { useEffect, useRef } from 'react';

import { type Query, useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { StarterDashboardV2PersonalizedTasksPollQuery } from 'src/app/pages/starterDashboardV2/shared/api';
import { getPersonalizedTasks } from 'src/app/pages/starterDashboardV2/shared/api';

import { FAST_POLL_MAX_COUNT, FAST_POLLING_INTERVAL, SLOW_POLLING_INTERVAL } from '../../constants';

const QUERY_KEY = 'personalizedTasks';

type PersonalizedTasksData = NonNullable<StarterDashboardV2PersonalizedTasksPollQuery['petition']>['personalizedTasks'];

export function isTerminalStatus(data: StarterDashboardV2PersonalizedTasksPollQuery | undefined): boolean {
	if (!data) return false;
	if (!data.petition) return true;
	const { personalizedTasks } = data.petition;
	if (personalizedTasks.__typename === 'PersonalizedTasksFailure') return true;
	return personalizedTasks.status === 'COMPLETED' || personalizedTasks.status === 'FAILED';
}

export function getPollingInterval(pollCount: number): number {
	return pollCount < FAST_POLL_MAX_COUNT ? FAST_POLLING_INTERVAL : SLOW_POLLING_INTERVAL;
}

export function usePollPersonalizedTasks({
	slug,
	shouldPoll,
}: {
	slug: string;
	shouldPoll: boolean;
}): PersonalizedTasksData | undefined {
	const utilityContext = useUtilityContext();
	const pollCountRef = useRef(0);

	useEffect(() => {
		pollCountRef.current = 0;
	}, [slug]);

	const { data } = useQuery<StarterDashboardV2PersonalizedTasksPollQuery>({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps -- utilityContext is stable and intentionally omitted from the key
		queryKey: [QUERY_KEY, slug],
		queryFn: async () => {
			pollCountRef.current += 1;
			try {
				const result = await getPersonalizedTasks(slug, utilityContext);
				if (!result) throw new Error('Failed to fetch personalized tasks');
				return result;
			} catch (error) {
				void utilityContext.errorReporter.report(
					{
						error: new Error('Failed to poll AI guidance tasks', {
							cause: error,
						}),
						context: {
							slug,
							pollCount: pollCountRef.current,
						},
					},
					{ severity: 'warning' },
				);
				throw error;
			}
		},
		enabled: shouldPoll,
		retry: false,
		refetchInterval: (query: Query<StarterDashboardV2PersonalizedTasksPollQuery, Error>) => {
			if (query.state.status === 'error') return false;
			if (isTerminalStatus(query.state.data)) return false;
			return getPollingInterval(pollCountRef.current);
		},
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

	if (!data?.petition) return undefined;
	return data.petition.personalizedTasks;
}
